import { type FC } from 'react';

import { useTranslation } from '@mint-lib/i18n';

import { Dialog, LoadingButton, Typography } from '@myn/mui';

import useSimpleNotification from '../../hooks/useSImpleNotification.js';
import { useDeleteVersion } from '../../utils/versionPageUtils.js';

interface Props {
  versionName: string;
  versionId: number | null;
  onClose: () => void;
}

const DeleteModal: FC<Props> = ({ versionName, versionId, onClose }) => {
  const { t } = useTranslation('@myn/planning');
  const { handleDeleteVersion: deleteVersion, isPending } = useDeleteVersion();
  const { showErrorNotification } = useSimpleNotification();

  const handleDeleteVersion = async () => {
    if (!versionId) {
      return;
    }

    try {
      await deleteVersion({
        versionId,
      });
      onClose();
    } catch {
      showErrorNotification({
        title: t('Error'),
        subTitle: t('Version was not deleted'),
      });
    }
  };

  return (
    <Dialog
      open={!!versionId}
      title={t('Are you sure you want to delete "{{versionName}}"?', {
        versionName,
      })}
      onClose={onClose}
      cancelButtonProps={{
        onClick: onClose,
      }}
      submitButton={
        <LoadingButton
          size="large"
          variant="contained"
          color="error"
          loading={isPending}
          onClick={handleDeleteVersion}
        >
          {t('Yes, delete')}
        </LoadingButton>
      }
    >
      <Typography>
        {t(
          'By deleting this version, you will lose all the data connected to it. Do you wish to continue?',
        )}
      </Typography>
    </Dialog>
  );
};

export default DeleteModal;
